/* component-facets.js — <facet-filters>
   Collection AJAX filtering via the Section Rendering API.
   - Listens for changes on filter checkboxes, price inputs, sort select, and
     clicks on active-filter "remove" / "clear all" links.
   - Builds the target URL from the form state, fetches
     `${url}&section_id=${sectionId}`, and swaps the product grid + facets + count
     from the returned HTML.
   - pushState so the URL reflects the filter state; popstate (back/forward) re-fetches.
   - Preserves focus by re-focusing the logical control after a swap.
   - Debounces price-range typing; submits immediately on checkbox/sort change.
   ES module, one component per file, no globals beyond window.KK (unused here). */

const DEBOUNCE_MS = 500;

class FacetFilters extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.getAttribute('data-section-id');
    this._debounce = null;

    this._onInput = (e) => this._handleInput(e);
    this._onSubmit = (e) => {
      // Only intercept the facet forms. A plain search box (no data-facets-form)
      // submits normally so a first-time search from an empty results page still
      // navigates even though there is nothing on the page to AJAX-swap yet.
      const form = e.target;
      if (!form || !form.matches || !form.matches('form[data-facets-form]')) return;
      e.preventDefault();
      this._apply(this._formURL(form), null);
    };
    this._onClick = (e) => this._handleClick(e);
    this._onToggle = (e) => this._handleToggle(e);
    this._onPop = () => this._apply(location.href, null, false);
    this._onDocClick = (e) => this._handleDocClick(e);

    this.addEventListener('input', this._onInput);
    this.addEventListener('change', this._onInput);
    this.addEventListener('click', this._onClick);
    this.addEventListener('submit', this._onSubmit);
    // `toggle` doesn't bubble, so listen in the capture phase to catch it on any pill.
    this.addEventListener('toggle', this._onToggle, true);
    window.addEventListener('popstate', this._onPop);
    document.addEventListener('click', this._onDocClick);
  }

  disconnectedCallback() {
    this.removeEventListener('input', this._onInput);
    this.removeEventListener('change', this._onInput);
    this.removeEventListener('click', this._onClick);
    this.removeEventListener('submit', this._onSubmit);
    this.removeEventListener('toggle', this._onToggle, true);
    window.removeEventListener('popstate', this._onPop);
    document.removeEventListener('click', this._onDocClick);
  }

  /* Filter-bar pills are native <details>. Opening one closes its siblings so only
     one dropdown shows at a time (matches the live dropdown behaviour). */
  _handleToggle(e) {
    const pill = e.target;
    if (!pill.matches || !pill.matches('[data-facet-pill]')) return;
    if (!pill.open) return;
    this.querySelectorAll('[data-facet-pill][open]').forEach((other) => {
      if (other !== pill) other.open = false;
    });
  }

  /* Click outside any open pill closes it. */
  _handleDocClick(e) {
    const open = this.querySelectorAll('[data-facet-pill][open]');
    if (!open.length) return;
    open.forEach((pill) => {
      if (!pill.contains(e.target)) pill.open = false;
    });
  }

  _handleInput(e) {
    const t = e.target;
    if (!t) return;
    if (t.matches('input[type="checkbox"]') || t.matches('select[name="sort_by"]')) {
      this._apply(this._formURL(t), t);
    } else if (t.matches('input[type="number"]')) {
      clearTimeout(this._debounce);
      this._debounce = setTimeout(() => this._apply(this._formURL(t), t), DEBOUNCE_MS);
    }
  }

  _handleClick(e) {
    const remove = e.target.closest('[data-facet-remove]');
    if (remove) {
      e.preventDefault();
      this._apply(remove.getAttribute('href'), null);
    }
  }

  /* Serialize the relevant form into a collection URL. The sort <select> lives in
     the toolbar but is associated to the desktop form via form="FacetForm", so
     el.form resolves it. Merge the sort value in even when a different form
     (e.g. the mobile facets form) triggered the change, so sort persists. */
  _formURL(el) {
    // el may be a form itself (submit event), a control inside a form (el.form),
    // or null — fall back to the first facets form in the component.
    let form = null;
    if (el && el.tagName === 'FORM') form = el;
    else if (el && el.form) form = el.form;
    if (!form) form = this.querySelector('form[data-facets-form]');
    if (!form) return location.href;
    const params = new URLSearchParams();
    for (const [k, v] of new FormData(form).entries()) {
      if (v !== '' && v != null) params.append(k, v);
    }
    // Ensure sort_by is present even if it belongs to the other form.
    if (!params.has('sort_by')) {
      const sort = this.querySelector('select[name="sort_by"]');
      if (sort && sort.value) params.set('sort_by', sort.value);
    }
    const base = form.getAttribute('action') || location.pathname;
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  async _apply(url, focusedEl, push = true) {
    this.setAttribute('aria-busy', 'true');
    const focusKey = this._focusKey(focusedEl);
    try {
      const fetchURL = this._withSection(url);
      const res = await fetch(fetchURL);
      if (!res.ok) throw new Error('Facet fetch failed: ' + res.status);
      const text = await res.text();
      this._swap(text);
      if (push) {
        const clean = this._stripSection(url);
        history.pushState({ facets: true }, '', clean);
      }
      this._restoreFocus(focusKey);
    } catch (err) {
      // On failure fall back to a full navigation so the user still gets results.
      window.location.href = this._stripSection(url);
      return;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  _withSection(url) {
    const u = new URL(url, location.origin);
    u.searchParams.set('section_id', this.sectionId);
    return u.toString();
  }

  _stripSection(url) {
    const u = new URL(url, location.origin);
    u.searchParams.delete('section_id');
    return u.pathname + (u.search ? u.search : '');
  }

  _swap(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    // Swap product grid, desktop facets, product count, and the mobile filter
    // panel by stable data hooks. Swapping [data-facets-mobile-panel] (the inner
    // container, not its <details> wrapper) keeps checkbox/count state in sync
    // for mobile users while preserving the toggle's open/closed state.
    ['[data-product-grid]', '[data-facets]', '[data-product-count]', '[data-facets-mobile-panel]'].forEach((sel) => {
      const next = parsed.querySelector(sel);
      const current = document.querySelector(sel);
      if (next && current) current.replaceWith(next);
    });
  }

  /* Build a stable key so we can re-focus the equivalent control after DOM swap. */
  _focusKey(el) {
    if (!el) return null;
    if (el.id) return '#' + el.id;
    if (el.name) return `[name="${el.name}"]${el.value ? `[value="${el.value}"]` : ''}`;
    return null;
  }

  _restoreFocus(key) {
    if (!key) return;
    const el = document.querySelector(key);
    if (el && typeof el.focus === 'function') el.focus();
  }
}

if (!customElements.get('facet-filters')) {
  customElements.define('facet-filters', FacetFilters);
}
